/**
 * Try-on modal -- fullscreen overlay that hosts the camera feed and 3-D canvas.
 *
 * Built with vanilla DOM; no framework required.
 * Manages keyboard (ESC) and pointer interactions, accessibility attributes,
 * and the video + canvas overlay layout.
 *
 * CSS class prefix is configurable via `cssPrefix` (default: 'mb-'). This
 * allows each widget to use its own namespace (ft-, fj-, fm-) while sharing
 * the structural code.
 */

import type { MediaPort } from '../ports/media-port.js';
import { BrowserMediaPort } from '../ports/browser-media-port.js';

/** Callback invoked when the modal requests closure. */
export type CloseCallback = () => void;

/**
 * Locale string resolver function.
 * Widgets pass their own i18n lookup (e.g. `(key) => t(locale, key)`) so
 * the modal can display localised text without depending on a specific i18n module.
 */
export type LocaleResolver = (key: string) => string;

/** Default no-op locale resolver that returns the key itself. */
const defaultLocale: LocaleResolver = (key: string) => {
  // Return sensible English defaults for known keys
  const defaults: Record<string, string> = {
    'modal.ariaLabel': 'Virtual try-on',
    'modal.close': 'Close',
    'modal.switchCamera': 'Switch camera',
    'modal.snapshot': 'Take snapshot',
    'tracking.alignFace': 'Align your face',
    'product.catalog': 'Product catalog',
    'mode.live': 'Live',
    'mode.photo': 'Photo',
    'photo.uploadTitle': 'Upload a photo',
    'photo.uploadHint': 'Drag & drop or click to select',
    'cart.addToCart': 'Add to Cart',
    'snapshot.download': 'Download',
    'snapshot.share': 'Share',
    'snapshot.copyLink': 'Copy link',
    'pd.checkButton': 'Check PD',
    'pd.measuring': 'Measuring…',
    'pd.result': 'Your PD: ~{pdMm} mm',
    'pd.disclaimer': 'Estimate for frame fitting. For prescription lenses, confirm with your optician. Mirrably is not a medical device.',
    'pd.error': 'Could not measure PD. Please face the camera directly.',
  };
  return defaults[key] ?? key;
};

/** References to key elements within the modal for external manipulation. */
export interface ModalElements {
  overlay: HTMLDivElement;
  container: HTMLDivElement;
  video: HTMLVideoElement;
  canvas: HTMLCanvasElement;
  catalogStrip: HTMLDivElement;
  closeBtn: HTMLButtonElement;
  switchCameraBtn: HTMLButtonElement;
  /** Button that triggers the snapshot/download flow. */
  snapshotBtn: HTMLButtonElement;
  trackingBadge: HTMLDivElement;
  stateScreen: HTMLDivElement;
  /** Mode toggle bar -- Live / Photo tabs. */
  modeToggle: HTMLDivElement;
  /** Drop-zone shown when Photo mode is active. */
  photoUploadArea: HTMLDivElement;
  /** Hidden file input bound to the photo upload area. */
  photoInput: HTMLInputElement;
  /** Image element that shows the uploaded photo in place of the video. */
  photoImg: HTMLImageElement;
  /** Toast shown after a snapshot is captured. Hidden by default. */
  snapshotToast: HTMLDivElement;
  /** Bar at the top of the container showing the current product name and price. */
  productInfoBar: HTMLDivElement;
  /** Add-to-cart button shown in the product info bar. */
  addToCartBtn: HTMLButtonElement;
  /** Button that triggers the PD (pupillary distance) measurement flow. */
  checkPdBtn: HTMLButtonElement;
  /** Inline element that shows the PD measurement result or status. */
  pdResultEl: HTMLSpanElement;
  /**
   * Size selector container element. Present only when `enableSizeSelector` is true.
   * Shown/hidden dynamically via `updateSizeSelector()`.
   */
  sizeSelectorEl: HTMLDivElement;
}

/**
 * Payload delivered via the `onPDMeasure` callback and the
 * `pd-measured` CustomEvent.
 */
export interface PDMeasurePayload {
  /** Median PD across all valid samples in mm. */
  pdMm: number;
  /** Number of valid frames used. */
  sampleCount: number;
  /** Quality rating based on measurement stability. */
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Provider of per-frame landmark data for PD measurement.
 *
 * The widget passes in its own live-landmark callback so that `createModal`
 * does not depend on the tracking package directly.
 *
 * @param callback - Called with each frame's normalised landmark array,
 *                   video dimensions, and head roll (radians). Return
 *                   false from the callback to stop sampling.
 * @returns A cleanup function to cancel the subscription.
 */
export type PDLandmarkProvider = (
  callback: (
    landmarks: Array<{ x: number; y: number; z?: number }>,
    videoWidth: number,
    videoHeight: number,
    roll: number,
  ) => boolean,
) => () => void;

/**
 * Payload delivered via the `size-change` CustomEvent when the user
 * selects a frame size from the size selector.
 */
export interface SizeChangePayload {
  /** The product whose size was selected. */
  productId: string;
  /** The selected size string (e.g. "M", "50mm"). */
  size: string;
}

/** Options for createModal. */
export interface CreateModalOptions {
  onClose: CloseCallback;
  /** Locale string resolver. Defaults to English built-in strings. */
  t?: LocaleResolver;
  /** CSS class prefix. Defaults to 'mb-'. */
  cssPrefix?: string;
  /**
   * Called when PD measurement completes successfully.
   * Widgets should wire this up to emit the `pd-measured` CustomEvent.
   */
  onPDMeasure?: (result: PDMeasurePayload) => void;
  /**
   * Provider for per-frame landmark data used during PD measurement.
   * If omitted, the "Check PD" button is hidden.
   */
  pdLandmarkProvider?: PDLandmarkProvider;
  /**
   * When true (default), the size selector strip is included in the modal.
   * Set to false to suppress the element (e.g. non-eyewear products).
   */
  enableSizeSelector?: boolean;
  /**
   * Hide the video element at construction time for non-video overlay widgets.
   * The element is still returned for compatibility with shared modal layouts.
   */
  hideVideo?: boolean;
  /** CSS class for the catalog/product slot. Defaults to `${cssPrefix}catalog-strip`. */
  catalogStripClassName?: string;
  /** Accessible label for the catalog/product slot. Defaults to `product.catalog`. */
  catalogStripAriaLabel?: string;
  /**
   * Called when the user selects a size from the size selector.
   */
  onSizeChange?: (payload: SizeChangePayload) => void;
  /**
   * Optional selectors that should be hidden immediately after modal creation.
   * Values are treated as class names (without dot) when no selector prefix is
   * provided.
   */
  hiddenSelectors?: string[];
  /**
   * Optional MediaPort to use for clipboard operations in the snapshot share path.
   * Defaults to BrowserMediaPort (uses navigator.clipboard with legacy textarea fallback).
   * Inject a fake port in tests or when the host environment has a custom clipboard.
   */
  mediaPort?: MediaPort;
}

/**
 * Create and return the try-on modal DOM structure.
 *
 * The modal is NOT appended to any container -- callers must insert it into the
 * Shadow DOM root themselves. Call `showModal()` to make it visible.
 *
 * @param options - Closure callback, locale resolver, and CSS prefix.
 * @returns Object containing all relevant element references.
 */
export function createModal(options: CreateModalOptions): ModalElements {
  const { onClose, cssPrefix = 'mb-' } = options;
  const t = options.t ?? defaultLocale;
  const p = cssPrefix; // shorthand
  const { onPDMeasure, pdLandmarkProvider } = options;
  const enableSizeSelector = options.enableSizeSelector !== false; // default true
  const hiddenSelectors = options.hiddenSelectors ?? [];

  // -- Overlay (dim background) -----------------------------------------------
  const overlay = document.createElement('div');
  overlay.className = `${p}modal-overlay`;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', t('modal.ariaLabel'));
  overlay.tabIndex = -1; // allow programmatic focus

  // Close on backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) onClose();
  });

  // Prevent pinch-zoom / pan gestures on iOS Safari while modal is open.
  const preventGesture = (e: Event) => e.preventDefault();
  for (const type of ['gesturestart', 'gesturechange', 'gestureend'] as const) {
    overlay.addEventListener(type, preventGesture, { passive: false });
  }
  overlay.addEventListener(
    'touchmove',
    (e) => { if (e.touches.length > 1) e.preventDefault(); },
    { passive: false },
  );

  // ESC key + focus trap
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    // Focus trap: cycle Tab among focusable elements inside the modal
    if (e.key === 'Tab') {
      const focusable = overlay.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, a[href]',
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  overlay.addEventListener('keydown', handleKeydown);

  // -- Main container ---------------------------------------------------------
  const container = document.createElement('div');
  container.className = `${p}tryon-container`;

  // -- Video feed -------------------------------------------------------------
  const video = document.createElement('video');
  video.className = `${p}video-layer`;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.muted = true;
  video.autoplay = true;
  video.setAttribute('aria-hidden', 'true');
  if (options.hideVideo === true) {
    video.style.display = 'none';
  }

  // -- 3-D overlay canvas -----------------------------------------------------
  const canvas = document.createElement('canvas');
  canvas.className = `${p}canvas-overlay`;
  canvas.setAttribute('aria-hidden', 'true');

  // -- Controls bar -----------------------------------------------------------
  const controls = document.createElement('div');
  controls.className = `${p}controls`;

  const closeBtn = document.createElement('button');
  closeBtn.className = `${p}close-btn`;
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', t('modal.close'));
  closeBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" stroke-width="2.5"
         stroke-linecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  `;
  closeBtn.addEventListener('click', onClose);

  const switchCameraBtn = document.createElement('button');
  switchCameraBtn.className = `${p}switch-camera-btn`;
  switchCameraBtn.type = 'button';
  switchCameraBtn.setAttribute('aria-label', t('modal.switchCamera'));
  switchCameraBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M20 7h-9"/>
      <path d="M14 17H5"/>
      <circle cx="17" cy="17" r="3"/>
      <circle cx="7" cy="7" r="3"/>
    </svg>
  `;

  // -- Snapshot button --------------------------------------------------------
  const snapshotBtn = document.createElement('button');
  snapshotBtn.className = `${p}snapshot-btn`;
  snapshotBtn.type = 'button';
  snapshotBtn.setAttribute('aria-label', t('modal.snapshot'));
  snapshotBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8
               a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  `;

  controls.appendChild(closeBtn);
  controls.appendChild(snapshotBtn);
  controls.appendChild(switchCameraBtn);

  // -- Tracking badge ---------------------------------------------------------
  const trackingBadge = document.createElement('div');
  trackingBadge.className = `${p}tracking-badge`;
  trackingBadge.setAttribute('aria-live', 'polite');
  trackingBadge.textContent = t('tracking.alignFace');
  trackingBadge.style.opacity = '0'; // Hidden until needed

  // -- State screen (loading / permission denied) -----------------------------
  const stateScreen = document.createElement('div');
  stateScreen.className = `${p}state-screen`;
  stateScreen.setAttribute('aria-live', 'assertive');
  stateScreen.style.display = 'none';

  // -- Catalog strip ----------------------------------------------------------
  const catalogStrip = document.createElement('div');
  catalogStrip.className = options.catalogStripClassName ?? `${p}catalog-strip`;
  catalogStrip.setAttribute('aria-label', options.catalogStripAriaLabel ?? t('product.catalog'));

  // -- Mode toggle (Live | Photo tabs) ----------------------------------------
  const modeToggle = document.createElement('div');
  modeToggle.className = `${p}mode-toggle`;
  modeToggle.setAttribute('role', 'tablist');
  modeToggle.setAttribute('aria-label', t('modal.ariaLabel'));

  const liveTab = document.createElement('button');
  liveTab.className = `${p}mode-toggle__tab ${p}mode-toggle__tab--active`;
  liveTab.type = 'button';
  liveTab.setAttribute('role', 'tab');
  liveTab.setAttribute('aria-selected', 'true');
  liveTab.setAttribute('aria-label', 'Live camera mode');
  liveTab.dataset['mode'] = 'live';
  liveTab.innerHTML = `
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
         stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M23 7 16 12 23 17z"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
    ${t('mode.live')}
  `;

  const photoTab = document.createElement('button');
  photoTab.className = `${p}mode-toggle__tab`;
  photoTab.type = 'button';
  photoTab.setAttribute('role', 'tab');
  photoTab.setAttribute('aria-selected', 'false');
  photoTab.setAttribute('aria-label', 'Photo upload mode');
  photoTab.dataset['mode'] = 'photo';
  photoTab.innerHTML = `
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
         stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
    ${t('mode.photo')}
  `;

  modeToggle.appendChild(liveTab);
  // Photo mode hidden until ready -- infrastructure kept intact for re-enable.
  photoTab.style.display = 'none';
  modeToggle.appendChild(photoTab);

  // -- Hidden file input ------------------------------------------------------
  const photoInput = document.createElement('input');
  photoInput.type = 'file';
  photoInput.accept = 'image/jpeg,image/png,image/webp';
  photoInput.setAttribute('capture', 'user');
  photoInput.className = `${p}photo-input`;
  photoInput.setAttribute('aria-hidden', 'true');
  photoInput.tabIndex = -1;

  // -- Photo upload area ------------------------------------------------------
  const photoUploadArea = document.createElement('div');
  photoUploadArea.className = `${p}photo-upload`;
  photoUploadArea.setAttribute('role', 'button');
  photoUploadArea.setAttribute('tabindex', '0');
  photoUploadArea.setAttribute('aria-label', t('photo.uploadTitle'));
  photoUploadArea.style.display = 'none';
  photoUploadArea.innerHTML = `
    <svg class="${p}photo-upload__icon" viewBox="0 0 24 24" width="40" height="40" fill="none"
         stroke="currentColor" stroke-width="1.5"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
    <p class="${p}photo-upload__title">${t('photo.uploadTitle')}</p>
    <p class="${p}photo-upload__hint">${t('photo.uploadHint')}</p>
  `;
  photoUploadArea.appendChild(photoInput);

  // -- Photo image (replaces video in photo mode) -----------------------------
  const photoImg = document.createElement('img');
  photoImg.className = `${p}photo-preview`;
  photoImg.setAttribute('alt', '');
  photoImg.setAttribute('aria-hidden', 'true');
  photoImg.style.display = 'none';

  // -- Product info bar -------------------------------------------------------
  const productInfoBar = document.createElement('div');
  productInfoBar.className = `${p}product-info`;
  productInfoBar.setAttribute('aria-live', 'polite');

  const productNameEl = document.createElement('span');
  productNameEl.className = `${p}product-info__name`;

  const productPriceEl = document.createElement('span');
  productPriceEl.className = `${p}product-info__price`;

  const addToCartBtn = document.createElement('button');
  addToCartBtn.type = 'button';
  addToCartBtn.className = `${p}add-to-cart-btn`;
  addToCartBtn.textContent = t('cart.addToCart');
  addToCartBtn.style.display = 'none';

  // -- Check PD button + result display --------------------------------------
  const checkPdBtn = document.createElement('button');
  checkPdBtn.type = 'button';
  checkPdBtn.className = `${p}check-pd-btn`;
  checkPdBtn.setAttribute('aria-label', t('pd.checkButton'));
  checkPdBtn.textContent = t('pd.checkButton');
  // Hide the button when no landmark provider is supplied
  checkPdBtn.style.display = pdLandmarkProvider ? '' : 'none';

  const pdResultEl = document.createElement('span');
  pdResultEl.className = `${p}pd-result`;
  pdResultEl.setAttribute('aria-live', 'polite');
  pdResultEl.style.display = 'none';

  // Wire up the PD measurement flow when a provider is supplied
  if (pdLandmarkProvider) {
    checkPdBtn.addEventListener('click', () => {
      triggerPDMeasurement({
        checkPdBtn,
        pdResultEl,
        pdLandmarkProvider,
        onPDMeasure,
        t,
        cssPrefix: p,
      });
    });
  }

  // -- Size selector (rendered below the variant strip, above catalog) ---------
  // Hidden by default; shown by updateSizeSelector() when product has sizes.
  const sizeSelectorEl = document.createElement('div');
  sizeSelectorEl.className = `${p}size-selector`;
  sizeSelectorEl.setAttribute('role', 'radiogroup');
  sizeSelectorEl.setAttribute('aria-label', 'Frame size');
  sizeSelectorEl.style.display = 'none';

  if (!enableSizeSelector) {
    sizeSelectorEl.dataset['disabled'] = 'true';
  }

  productInfoBar.appendChild(productNameEl);
  productInfoBar.appendChild(productPriceEl);
  productInfoBar.appendChild(addToCartBtn);
  productInfoBar.appendChild(checkPdBtn);
  productInfoBar.appendChild(pdResultEl);

  // -- Snapshot toast (hidden by default) -------------------------------------
  const snapshotToast = document.createElement('div');
  snapshotToast.className = `${p}snapshot-toast`;
  snapshotToast.setAttribute('role', 'status');
  snapshotToast.setAttribute('aria-live', 'polite');
  snapshotToast.style.display = 'none';

  // -- Assemble ---------------------------------------------------------------
  container.appendChild(video);
  container.appendChild(photoImg);
  container.appendChild(canvas);
  container.appendChild(productInfoBar);
  container.appendChild(controls);
  container.appendChild(modeToggle);
  container.appendChild(trackingBadge);
  container.appendChild(sizeSelectorEl);
  container.appendChild(catalogStrip);
  container.appendChild(photoUploadArea);
  container.appendChild(snapshotToast);
  container.appendChild(stateScreen);
  overlay.appendChild(container);

  for (const selector of hiddenSelectors) {
    const trimmed = selector.trim();
    if (!trimmed) continue;

    const query = trimmed.startsWith('.') || trimmed.startsWith('#')
      ? trimmed
      : `.${p}${trimmed}`;
    overlay.querySelectorAll<HTMLElement>(query).forEach((el) => {
      el.style.display = 'none';
    });
  }

  return {
    overlay,
    container,
    video,
    canvas,
    catalogStrip,
    closeBtn,
    switchCameraBtn,
    snapshotBtn,
    trackingBadge,
    stateScreen,
    modeToggle,
    photoUploadArea,
    photoInput,
    photoImg,
    snapshotToast,
    productInfoBar,
    addToCartBtn,
    checkPdBtn,
    pdResultEl,
    sizeSelectorEl,
  };
}

/**
 * Animate the modal overlay into view.
 * Call after appending to the DOM so the CSS transition fires.
 *
 * @param overlay   - The overlay element returned by createModal.
 * @param cssPrefix - CSS prefix (default: 'mb-').
 */
export function showModal(overlay: HTMLDivElement, cssPrefix = 'mb-'): void {
  // Defer one microtask so the browser has painted the initial opacity: 0 state
  requestAnimationFrame(() => {
    overlay.classList.add(`${cssPrefix}modal-overlay--visible`);
  });
  // Move focus into the modal for accessibility
  overlay.focus();
}

/**
 * Animate the modal overlay out of view.
 *
 * @param overlay   - The overlay element.
 * @param onDone    - Called after the transition completes (to remove from DOM).
 * @param cssPrefix - CSS prefix (default: 'mb-').
 */
export function hideModal(overlay: HTMLDivElement, onDone: () => void, cssPrefix = 'mb-'): void {
  overlay.classList.remove(`${cssPrefix}modal-overlay--visible`);
  let called = false;
  const done = () => {
    if (called) return;
    called = true;
    onDone();
  };
  overlay.addEventListener('transitionend', done, { once: true });
  // Fallback: if transitionend never fires (no CSS transition, display:none, etc.)
  // clean up after 500ms to prevent leaked DOM nodes.
  setTimeout(done, 500);
}

/**
 * Show a full-screen state message inside the modal container.
 *
 * Used for "Loading...", "Camera permission denied", and similar blocking states.
 *
 * @param stateScreen - The state screen element from ModalElements.
 * @param title       - Short heading text.
 * @param message     - Longer description text.
 * @param cssPrefix   - CSS prefix (default: 'mb-').
 * @param actionLabel - Optional CTA button label.
 * @param onAction    - Called when the CTA button is clicked.
 */
export function showStateScreen(
  stateScreen: HTMLDivElement,
  title: string,
  message: string,
  cssPrefix = 'mb-',
  actionLabel?: string,
  onAction?: () => void,
): void {
  const p = cssPrefix;
  stateScreen.innerHTML = '';
  stateScreen.style.display = 'flex';

  const spinner = document.createElement('div');
  spinner.className = `${p}spinner`;

  const titleEl = document.createElement('p');
  titleEl.className = `${p}state-screen__title`;
  titleEl.textContent = title;

  const msgEl = document.createElement('p');
  msgEl.className = `${p}state-screen__message`;
  msgEl.textContent = message;

  stateScreen.appendChild(spinner);
  stateScreen.appendChild(titleEl);
  stateScreen.appendChild(msgEl);

  if (actionLabel && onAction) {
    const btn = document.createElement('button');
    btn.className = `${p}state-screen__btn`;
    btn.type = 'button';
    btn.textContent = actionLabel;
    btn.addEventListener('click', onAction);
    stateScreen.appendChild(btn);
  }
}

/** Hide the state screen and return to the camera view. */
export function hideStateScreen(stateScreen: HTMLDivElement): void {
  stateScreen.style.display = 'none';
  stateScreen.innerHTML = '';
}

/**
 * Show the snapshot toast at the bottom of the modal.
 *
 * Renders a thumbnail of the captured image alongside "Download" and optional
 * "Share" action buttons. Auto-dismisses after 5 seconds.
 *
 * @param toast       - The snapshotToast element from ModalElements.
 * @param blob        - The captured PNG Blob.
 * @param productName - Product name used as the download filename stem.
 * @param cssPrefix   - CSS prefix (default: 'mb-').
 * @param t           - Locale resolver (default: English built-in).
 * @param mediaPort   - Optional MediaPort for clipboard operations.
 *                      Defaults to BrowserMediaPort (navigator.clipboard with
 *                      legacy textarea fallback for older Safari).
 */
export function showSnapshotToast(
  toast: HTMLDivElement,
  blob: Blob,
  productName: string,
  cssPrefix = 'mb-',
  t: LocaleResolver = defaultLocale,
  mediaPort?: MediaPort,
): void {
  const port: MediaPort = mediaPort ?? new BrowserMediaPort();
  const p = cssPrefix;

  // Clear any previous content and cancel pending auto-dismiss
  toast.innerHTML = '';
  const existingTimer = (toast as HTMLDivElement & { _dismissTimer?: ReturnType<typeof setTimeout> })._dismissTimer;
  if (existingTimer !== undefined) clearTimeout(existingTimer);

  const objectUrl = URL.createObjectURL(blob);

  // Thumbnail
  const thumb = document.createElement('img');
  thumb.className = `${p}snapshot-toast__thumb`;
  thumb.src = objectUrl;
  thumb.alt = 'Snapshot preview';

  // Actions container
  const actions = document.createElement('div');
  actions.className = `${p}snapshot-toast__actions`;

  // Download button
  const downloadBtn = document.createElement('button');
  downloadBtn.type = 'button';
  downloadBtn.className = `${p}snapshot-toast__btn`;
  downloadBtn.textContent = t('snapshot.download');
  downloadBtn.addEventListener('click', () => {
    const a = document.createElement('a');
    a.href = objectUrl;
    const safeName = productName.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'tryon';
    a.download = `${safeName}-tryon.png`;
    a.click();
    hideSnapshotToast(toast, cssPrefix);
  });

  // Share button -- only rendered when Web Share API is available
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    const shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.className = `${p}snapshot-toast__btn ${p}snapshot-toast__btn--secondary`;
    shareBtn.textContent = t('snapshot.share');
    shareBtn.addEventListener('click', () => {
      const file = new File([blob], `${productName || 'tryon'}.png`, { type: 'image/png' });
      navigator
        .share({ files: [file], title: `Try-on: ${productName}` })
        .catch(() => {
          // Share cancelled or failed -- fall back silently
        });
      hideSnapshotToast(toast, cssPrefix);
    });
    actions.appendChild(shareBtn);
  } else {
    // Fallback: copy object URL to clipboard when Web Share is unavailable
    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = `${p}snapshot-toast__btn ${p}snapshot-toast__btn--secondary`;
    copyBtn.textContent = t('snapshot.copyLink');
    copyBtn.addEventListener('click', () => {
      port.clipboard.writeText(objectUrl).catch(() => {/* no-op */});
      hideSnapshotToast(toast, cssPrefix);
    });
    actions.appendChild(copyBtn);
  }

  actions.insertBefore(downloadBtn, actions.firstChild);

  toast.appendChild(thumb);
  toast.appendChild(actions);

  // Show with animation
  toast.style.display = 'flex';
  requestAnimationFrame(() => {
    toast.classList.add(`${p}snapshot-toast--visible`);
  });

  // Auto-dismiss after 5 s
  const timer = setTimeout(() => hideSnapshotToast(toast, cssPrefix), 5000);
  (toast as HTMLDivElement & { _dismissTimer?: ReturnType<typeof setTimeout> })._dismissTimer = timer;

  // Release the object URL once the image has loaded
  thumb.addEventListener('load', () => URL.revokeObjectURL(objectUrl), { once: true });
}

/**
 * Hide the snapshot toast and clean up its DOM content.
 *
 * @param toast     - The snapshotToast element from ModalElements.
 * @param cssPrefix - CSS prefix (default: 'mb-').
 */
export function hideSnapshotToast(toast: HTMLDivElement, cssPrefix = 'mb-'): void {
  toast.classList.remove(`${cssPrefix}snapshot-toast--visible`);
  const timer = (toast as HTMLDivElement & { _dismissTimer?: ReturnType<typeof setTimeout> })._dismissTimer;
  if (timer !== undefined) {
    clearTimeout(timer);
    delete (toast as HTMLDivElement & { _dismissTimer?: ReturnType<typeof setTimeout> })._dismissTimer;
  }
  // Remove content after transition completes (with timeout fallback)
  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    toast.style.display = 'none';
    toast.innerHTML = '';
  };
  toast.addEventListener('transitionend', cleanup, { once: true });
  setTimeout(cleanup, 500);
}

// ---------------------------------------------------------------------------
// PD Measurement
// ---------------------------------------------------------------------------

/** Configuration for a single PD measurement session. */
export interface TriggerPDMeasurementOptions {
  /** The "Check PD" button element. */
  checkPdBtn: HTMLButtonElement;
  /** The inline result display element. */
  pdResultEl: HTMLSpanElement;
  /** Landmark data provider (from the widget's tracking loop). */
  pdLandmarkProvider: PDLandmarkProvider;
  /** Called on success with the final PDMeasurePayload. */
  onPDMeasure?: ((result: PDMeasurePayload) => void) | undefined;
  /** Locale resolver. */
  t?: LocaleResolver;
  /** CSS class prefix. */
  cssPrefix?: string;
}

/**
 * Run a PD measurement session.
 *
 * Collects up to 30 valid frames (max 5 s), computes the median PD,
 * displays the result for 8 s, persists to localStorage, and calls
 * `onPDMeasure`.
 *
 * Error path: if fewer than 10 valid samples are collected within 3 s,
 * shows an error message and resets.
 *
 * A11y: the button has `aria-label`; the result element is `aria-live="polite"`.
 */
export function triggerPDMeasurement(opts: TriggerPDMeasurementOptions): void {
  const {
    checkPdBtn,
    pdResultEl,
    pdLandmarkProvider,
    onPDMeasure,
    cssPrefix = 'mb-',
  } = opts;
  const t = opts.t ?? defaultLocale;
  const p = cssPrefix;

  const TARGET_SAMPLES  = 30;
  const MIN_SAMPLES     = 10;
  const MAX_DURATION_MS = 5_000;
  const ERROR_CHECK_MS  = 3_000;
  const RESULT_DISPLAY_MS = 8_000;

  // Disable button and show measuring state
  checkPdBtn.disabled = true;
  checkPdBtn.textContent = t('pd.measuring');
  pdResultEl.style.display = 'none';
  pdResultEl.textContent = '';

  const samples: number[] = [];
  const startTime = Date.now();
  let stopSampling: (() => void) | null = null;
  let settled = false;

  const finish = (error?: string) => {
    if (settled) return;
    settled = true;
    if (stopSampling) {
      stopSampling();
      stopSampling = null;
    }

    if (error) {
      pdResultEl.textContent = error;
      pdResultEl.style.display = '';
      pdResultEl.className = `${p}pd-result ${p}pd-result--error`;
      checkPdBtn.disabled = false;
      checkPdBtn.textContent = t('pd.checkButton');
      // Auto-hide the error after 5 s
      setTimeout(() => {
        pdResultEl.style.display = 'none';
        pdResultEl.textContent = '';
      }, 5_000);
      return;
    }

    // Aggregate samples
    const sorted = [...samples].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const pdMm = sorted.length % 2 === 0
      ? Math.round(((sorted[mid - 1]! + sorted[mid]!) / 2) * 10) / 10
      : Math.round(sorted[mid]! * 10) / 10;

    const mean = samples.reduce((s, v) => s + v, 0) / samples.length;
    const variance = samples.reduce((s, v) => s + (v - mean) ** 2, 0) / (samples.length - 1 || 1);
    const sd = Math.sqrt(variance);

    let confidence: PDMeasurePayload['confidence'];
    if (sd < 1.5) {
      confidence = 'high';
    } else if (sd < 3) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }

    const payload: PDMeasurePayload = { pdMm, sampleCount: samples.length, confidence };

    // Persist to localStorage
    try {
      localStorage.setItem('mirrably:calib_pdMm', String(pdMm));
    } catch {
      // localStorage unavailable (private browsing, quota exceeded) — non-fatal
    }

    // Display result
    const resultText = t('pd.result').replace('{pdMm}', String(Math.round(pdMm)));
    pdResultEl.textContent = resultText;
    pdResultEl.style.display = '';
    pdResultEl.className = `${p}pd-result ${p}pd-result--success`;

    // Disclaimer
    const disclaimer = document.createElement('small');
    disclaimer.className = `${p}pd-result__disclaimer`;
    disclaimer.textContent = t('pd.disclaimer');
    pdResultEl.appendChild(document.createElement('br'));
    pdResultEl.appendChild(disclaimer);

    checkPdBtn.disabled = false;
    checkPdBtn.textContent = t('pd.checkButton');

    // Call back to widget for event emission
    onPDMeasure?.(payload);

    // Auto-hide result after 8 s
    setTimeout(() => {
      pdResultEl.style.display = 'none';
      pdResultEl.textContent = '';
      pdResultEl.className = `${p}pd-result`;
    }, RESULT_DISPLAY_MS);
  };

  // Kick off sampling via the provider
  stopSampling = pdLandmarkProvider((landmarks, videoWidth, videoHeight, roll) => {
    if (settled) return false;

    const elapsed = Date.now() - startTime;

    // Import-free inline implementation of measurePDFromIris logic
    // (avoids a circular dep from core → tracking package at runtime;
    //  the widget wires up a provider that does the math internally)
    const MAX_ROLL = 0.0873; // ~5°
    if (Math.abs(roll) > MAX_ROLL) {
      // Invalid pose — check error threshold
      if (elapsed >= ERROR_CHECK_MS && samples.length < MIN_SAMPLES) {
        finish(t('pd.error'));
        return false;
      }
      return true;
    }

    // Extract iris rings
    const leftRing = [469, 470, 471, 472]
      .map((i) => landmarks[i])
      .filter(Boolean) as Array<{ x: number; y: number }>;
    const rightRing = [474, 475, 476, 477]
      .map((i) => landmarks[i])
      .filter(Boolean) as Array<{ x: number; y: number }>;

    if (leftRing.length < 2 || rightRing.length < 2) return true;

    const diagPx = (ring: Array<{ x: number; y: number }>) => {
      const xs = ring.map((lm) => lm.x * videoWidth);
      return Math.max(...xs) - Math.min(...xs);
    };
    const irisPx = (diagPx(leftRing) + diagPx(rightRing)) / 2;
    if (irisPx <= 0) return true;

    const mmPerPx = 11.7 / irisPx;
    const lc = landmarks[468];
    const rc = landmarks[473];
    if (!lc || !rc) return true;

    const dxPx = (rc.x - lc.x) * videoWidth;
    const dyPx = (rc.y - lc.y) * videoHeight;
    const pdMmInstant = Math.hypot(dxPx, dyPx) * Math.cos(roll) * mmPerPx;

    if (pdMmInstant >= 50 && pdMmInstant <= 80) {
      samples.push(pdMmInstant);
    }

    // Reached target — finish
    if (samples.length >= TARGET_SAMPLES) {
      finish();
      return false;
    }

    // Timeout check
    if (elapsed >= MAX_DURATION_MS) {
      if (samples.length >= MIN_SAMPLES) {
        finish();
      } else {
        finish(t('pd.error'));
      }
      return false;
    }

    // Error threshold check (3 s, < 10 valid samples)
    if (elapsed >= ERROR_CHECK_MS && samples.length < MIN_SAMPLES) {
      finish(t('pd.error'));
      return false;
    }

    return true;
  });
}

/**
 * Update the product info bar with the current product's name and price.
 *
 * Hides the bar entirely when no name is provided. Shows with a fade-in
 * animation on first call.
 *
 * @param bar       - The productInfoBar element from ModalElements.
 * @param name      - Product display name.
 * @param price     - Optional formatted price string (e.g. "$129").
 * @param cssPrefix - CSS prefix (default: 'mb-').
 */
export function updateProductInfo(bar: HTMLDivElement, name: string, price?: string, cssPrefix = 'mb-'): void {
  const p = cssPrefix;
  const nameEl = bar.querySelector<HTMLSpanElement>(`.${p}product-info__name`);
  const priceEl = bar.querySelector<HTMLSpanElement>(`.${p}product-info__price`);
  const cartBtn = bar.querySelector<HTMLButtonElement>(`.${p}add-to-cart-btn`);

  if (!name) {
    bar.style.display = 'none';
    if (cartBtn) cartBtn.style.display = 'none';
    return;
  }

  if (nameEl) nameEl.textContent = name;
  if (priceEl) {
    priceEl.textContent = price ?? '';
    priceEl.style.display = price ? '' : 'none';
  }
  if (cartBtn) cartBtn.style.display = '';

  if (bar.style.display === 'none' || !bar.classList.contains(`${p}product-info--visible`)) {
    bar.style.display = 'flex';
    requestAnimationFrame(() => {
      bar.classList.add(`${p}product-info--visible`);
    });
  }
}

// ---------------------------------------------------------------------------
// Size selector
// ---------------------------------------------------------------------------

/** localStorage key prefix for persisted size selections. */
const SIZE_STORAGE_PREFIX = 'mirrably:size:';

/**
 * Update the size selector strip for the active product.
 *
 * Shows the strip when `sizes` is non-empty; hides it otherwise.
 * Restores a previously persisted selection from localStorage.
 * Emits a `'size-change'` CustomEvent on the `sizeSelectorEl` when a pill is clicked.
 *
 * @param sizeSelectorEl - The size selector container element from `ModalElements`.
 * @param productId      - Active product identifier (used as localStorage key).
 * @param sizes          - Available size options. Empty array hides the selector.
 * @param onSizeChange   - Optional callback called on every selection change.
 * @param cssPrefix      - CSS prefix (default: 'mb-').
 */
export function updateSizeSelector(
  sizeSelectorEl: HTMLDivElement,
  productId: string,
  sizes: string[],
  onSizeChange?: (payload: SizeChangePayload) => void,
  cssPrefix = 'mb-',
): void {
  const p = cssPrefix;

  // Hide when disabled or no sizes available
  if (sizeSelectorEl.dataset['disabled'] === 'true' || !sizes || sizes.length === 0) {
    sizeSelectorEl.style.display = 'none';
    sizeSelectorEl.innerHTML = '';
    return;
  }

  sizeSelectorEl.innerHTML = '';
  sizeSelectorEl.style.display = '';

  // Restore previously persisted size for this product
  let storedSize: string | null = null;
  try {
    storedSize = localStorage.getItem(`${SIZE_STORAGE_PREFIX}${productId}`);
  } catch {
    // localStorage unavailable — non-fatal
  }

  // Default: stored size if valid, else first size
  const defaultSize = (storedSize && sizes.includes(storedSize)) ? storedSize : sizes[0]!;
  let selectedSize = defaultSize;

  const activate = (btn: HTMLButtonElement, size: string) => {
    // Deactivate all
    const all = sizeSelectorEl.querySelectorAll<HTMLButtonElement>(`.${p}size-pill`);
    for (const b of all) {
      b.setAttribute('aria-checked', 'false');
      b.classList.remove(`${p}size-pill--selected`);
    }
    // Activate selected
    btn.setAttribute('aria-checked', 'true');
    btn.classList.add(`${p}size-pill--selected`);
    selectedSize = size;

    // Persist
    try {
      localStorage.setItem(`${SIZE_STORAGE_PREFIX}${productId}`, size);
    } catch {
      // localStorage unavailable — non-fatal
    }

    const payload: SizeChangePayload = { productId, size };
    onSizeChange?.(payload);
    sizeSelectorEl.dispatchEvent(
      new CustomEvent('size-change', { bubbles: true, detail: payload }),
    );
  };

  for (const size of sizes) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `${p}size-pill`;
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-label', `Size ${size}`);
    btn.textContent = size;

    const isDefault = size === selectedSize;
    btn.setAttribute('aria-checked', isDefault ? 'true' : 'false');
    if (isDefault) btn.classList.add(`${p}size-pill--selected`);

    btn.addEventListener('click', () => activate(btn, size));

    sizeSelectorEl.appendChild(btn);
  }
}

/**
 * Retrieve the currently selected size for a product from localStorage.
 *
 * Useful when building the cart add-to-cart payload — pass the returned size
 * as `attributes: { size }` to the cart-bridge `addToCart()` call.
 *
 * @param productId - Product identifier.
 * @returns The persisted size string, or `null` if none stored.
 */
export function getSelectedSize(productId: string): string | null {
  try {
    return localStorage.getItem(`${SIZE_STORAGE_PREFIX}${productId}`);
  } catch {
    return null;
  }
}
